import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../context/ToastContext';
import PageTableLayout from '../../../components/PageTableLayout';
import apiService from '../../../services/apiService';
import { FaFilter, FaTimes, FaEye, FaPencilAlt } from 'react-icons/fa';
import { Button } from '../../../components/ui';

const breadcrumb = (
  <span>
    Home &nbsp;/&nbsp; <span className="text-gray-700">Course Programs</span>
  </span>
);

const CoursePrograms = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    name: '',
    course: '',
    eduStandard: '',
    status: ''
  });
  const [courses, setCourses] = useState([]);
  const [eduStandards, setEduStandards] = useState([]);
  const [limit, setLimit] = useState(10);
  const [isFiltered, setIsFiltered] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  const fetchCoursePrograms = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      // Check if any filters are applied for the toast notification
      const hasFilters = Object.values(filters).some(value => value !== '');
      setIsFiltering(hasFilters);
      
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      params.append('totalCount', true);
      params.append('populate', 'course,eduStandard');
      params.append('select_course', 'name');
      params.append('select_eduStandard', 'name');
      
      // Add any filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      
      const response = await apiService.request(`/i/ims/courseprogram?${params.toString()}`);
      
      setData(response.data);
      setTotalRecords(response.totalCount);
      setCurrentPage(page);
      
      if (isFiltering) {
        toast.showInfo(`Found ${response.data.length} results`);
      }
      
      // Check if any filters are applied
      setIsFiltered(Object.values(filters).some(value => value !== ''));
    } catch (error) {
      console.error('Error fetching course programs:', error);
      toast.showError('Failed to fetch course programs');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  // Fetch courses for filter dropdown
  const fetchCourses = useCallback(async () => {
    try {
      const courseData = await apiService.request('/i/ims/course?limit=all&status=true&select=name,eduStandard,eduStandards&populate=eduStandard&select_eduStandard=name');
      
      if (Array.isArray(courseData)) {
        setCourses(courseData);
      } else {
        console.error('Course data is not an array:', courseData);
        setCourses([]);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.showError('Failed to load course filter options');
    }
  }, []);

  // Fetch education standards for filter dropdown
  const fetchEduStandards = useCallback(async () => {
    try {
      const eduStandardData = await apiService.request('/i/ims/edustandard?limit=all&status=true&select=name');
      
      if (Array.isArray(eduStandardData)) {
        setEduStandards(eduStandardData);
      } else {
        console.error('Education standard data is not an array:', eduStandardData);
        setEduStandards([]);
      }
    } catch (error) {
      console.error('Error fetching education standards:', error);
      toast.showError('Failed to load education standard filter options');
    }
  }, []);

  useEffect(() => {
    fetchCoursePrograms(1);
    fetchCourses();
    fetchEduStandards();
  }, [fetchCoursePrograms, fetchCourses, fetchEduStandards]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterSubmit = () => {
    fetchCoursePrograms(1, filters);
  };

  const handleFilterReset = () => {
    const resetFilters = {
      name: '',
      course: '',
      eduStandard: '',
      status: ''
    };
    setFilters(resetFilters);
    fetchCoursePrograms(1, resetFilters);
  };

  const handlePageChange = (page) => {
    fetchCoursePrograms(page, filters);
  };

  const handleLimitChange = (value) => {
    const newLimit = Number(typeof value === 'object' ? value.target.value : value);
    setLimit(newLimit);
    fetchCoursePrograms(1, filters, newLimit);
  };

  const columns = [
    { 
      key: 'name', 
      title: 'Name',
      filterComponent: (
        <input 
          className="w-full border px-2 py-1 rounded text-xs" 
          placeholder="Filter by name"
          value={filters.name}
          onChange={(e) => handleFilterChange('name', e.target.value)}
        />
      )
    },
    { 
      key: 'uri', 
      title: 'Uri',
      filterComponent: null // No filter for URI column
    },
    { 
      key: 'course', 
      title: 'Course',
      render: (row) => row.course ? row.course.name : '-',
      filterComponent: (
        <select
          className="w-full border px-2 py-1 rounded text-xs"
          value={filters.course}
          onChange={(e) => handleFilterChange('course', e.target.value)}
          disabled={courses.length === 0}
        >
          <option value="">{courses.length === 0 ? 'Loading...' : 'All Courses'}</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      )
    },
    { 
      key: 'eduStandard', 
      title: 'Education Standard',
      render: (row) => row.eduStandard ? row.eduStandard.name : '-',
      filterComponent: (
        <select
          className="w-full border px-2 py-1 rounded text-xs"
          value={filters.eduStandard}
          onChange={(e) => handleFilterChange('eduStandard', e.target.value)}
          disabled={eduStandards.length === 0}
        >
          <option value="">{eduStandards.length === 0 ? 'Loading...' : 'All Standards'}</option>
          {eduStandards.map((std) => (
            <option key={std.id} value={std.id}>{std.name}</option>
          ))}
        </select>
      )
    },
    { 
      key: 'status', 
      title: 'Status',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${row.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {row.status ? 'Active' : 'Inactive'}
        </span>
      ),
      filterComponent: (
        <select 
          className="w-full border px-2 py-1 rounded text-xs"
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      )
    },
    {
      key: 'order',
      title: 'Order',
      filterComponent: null // No filter for order column
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (row) => (
        <div className="flex space-x-2">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/course-programs/view/${row.id}`);
            }}
            variant="outline"
            size="xs"
            title="View"
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
          >
            <FaEye className="h-4 w-4" />
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/course-programs/edit/${row.id}`);
            }}
            variant="outline"
            size="xs"
            title="Edit"
            className="text-amber-600 border-amber-600 hover:bg-amber-50"
          >
            <FaPencilAlt className="h-4 w-4" />
          </Button>
        </div>
      ),
      filterComponent: (
        <div className="flex space-x-1">
          <Button
            variant="outline"
            size="xs"
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
            onClick={handleFilterSubmit}
            disabled={!filters.name && !filters.course && !filters.eduStandard && !(["true","false"].includes(filters.status))}
          >
            <FaFilter className="h-3 w-3 mr-1" />
            Filter
          </Button>
          <Button
            variant="outline"
            size="xs"
            className="text-red-600 border-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleFilterReset}
            disabled={!isFiltered}
          >
            <FaTimes className="h-3 w-3 mr-1" />
            Reset
          </Button>
        </div>
      )
    }
  ];

  const handleView = (row) => {
    navigate(`/course-programs/view/${row.id}`);
  };

  const handleEdit = (row) => {
    navigate(`/course-programs/edit/${row.id}`);
  };

  const handleAddNew = () => {
    navigate('/course-programs/add');
  };

  const handleCourseClick = () => {
    navigate('/course-programs/courses');
  };

  const handleEduStandardClick = () => {
    navigate('/course-programs/edu-standard');
  };

  const actionButtons = [
    {
      label: 'Add New',
      onClick: handleAddNew,
      className: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      label: 'Course',
      onClick: handleCourseClick,
      className: 'bg-green-600 hover:bg-green-700'
    },
    {
      label: 'EduStandard',
      onClick: handleEduStandardClick,
      className: 'bg-purple-600 hover:bg-purple-700'
    }
  ];

  return (
 
    <PageTableLayout
      title="Course Programs"
      breadcrumb={breadcrumb}
      columns={columns}
      data={data || []}
      onView={handleView}
      onEdit={handleEdit}
      actions={actionButtons}
      loading={loading}
      pagination={{
        currentPage,
        totalRecords,
        totalPages: Math.ceil(totalRecords / limit),
        limit,
        onLimitChange: handleLimitChange
      }}
      onPageChange={handlePageChange}
      showFiltersInTable={true}
    />
  );
};

export default CoursePrograms;
